import SelettorePeriodo from '../../components/SelettorePeriodo';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../../api';
import { urlEliminazione, messaggioEliminazione, avvisaConflitto } from '../../utils/eliminazione';
import Layout from '../../components/Layout';
import stili from '../../components/competenze/stili';
import ClienteRicerca from '../../components/competenze/ClienteRicerca';
import ConflittoVersione, { eConflitto } from '../../components/ConflittoVersione';
import useAggiornamentoPeriodico from '../../hooks/useAggiornamentoPeriodico';
import useVersioniViste from '../../hooks/useVersioniViste';
import ModificataDaAltri from '../../components/ModificataDaAltri';
import { Link } from 'react-router-dom';
import { usePuoScrivere, puoAccedere } from '../../utils/permessi';
import { useAuth } from '../../context/AuthContext';
import { formattaData } from '../../utils/date';
import FormDitta from './FormDitta';
import UnilavCarica from './UnilavCarica';
import CampoTipoTrasformazione from '../../components/scadenziario/CampoTipoTrasformazione';
import { etichettaTipoTrasformazione, leggiTipoTrasformazione, scriviTipoTrasformazione } from '../../utils/campiScadenza';
import SceltaCcnl from '../../components/scadenziario/SceltaCcnl';
import BottoneInvio from '../../components/scadenziario/BottoneInvio';
import { regolaDi, mancanti, richiestiConCondizioni, TIPI_CONTRATTO, eIntermittente, tipologiaDaRiga, ORARI, NON_DEFINITO } from '../../utils/campiScadenza';
import '../competenze/competenze.css';

const ORIGINI = { manuale: 'Manuale', unilav_veneto: 'UNILAV', friuli: 'UNILAV' };
const FILTRO_ORIGINI = { manuale: 'Manuale', unilav: 'UNILAV' };

function meseCorrente() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
function oggi() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function nomeMese(mese) {
  const [a, m] = mese.split('-').map(Number);
  return new Date(a, m - 1, 1).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
}
// Date della tabella con l'anno a due cifre (molte colonne); gli export le hanno complete
const dataBreve = d => (d ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(2, 4)}` : '');
// Solo la matricola del dipendente (il codice azienda ha la sua colonna)
const matricola = r => r.matricola || '';

async function scarica(url, params, nomeFile) {
  const res = await api.get(url, { params, responseType: 'blob' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(res.data);
  link.download = nomeFile;
  link.click();
  setTimeout(() => URL.revokeObjectURL(link.href), 1000);
}

const COLONNE = [
  { chiave: 'codice_azienda', label: 'Cod. azienda', larghezza: '5.6%' },
  { chiave: 'ditta', label: 'Azienda', larghezza: '9.5%' },
  { chiave: 'lavoratore', label: 'Lavoratore', larghezza: '8.5%' },
  { chiave: null, label: 'Matricola', larghezza: '6%' },
  { chiave: 'categoria', label: 'Categoria', larghezza: '8%' },
  { chiave: null, label: 'Tipologia', larghezza: '9%' },
  { chiave: 'data_dal', label: 'Inizio rapporto', larghezza: '6.3%' },
  { chiave: 'data_scadenza', label: 'Scadenza', larghezza: '6.3%' },
  { chiave: null, label: 'Orario', larghezza: '6%' },
  { chiave: null, label: 'Ore', larghezza: '5.5%' },
  { chiave: null, label: 'Stag.', larghezza: '3.8%' },
  { chiave: null, label: 'Note', larghezza: '8%' },
  { chiave: 'operatore', label: 'Operatore', larghezza: '6.4%' },
];

export default function Scadenziario() {
  const puoScrivere = usePuoScrivere('scadenziario');
  // "Porta in Competenze": serve la scrittura su Competenze (e almeno la lettura dello Scadenziario, già necessaria per essere qui)
  const puoPortare = usePuoScrivere('competenze');
  const { utente } = useAuth();
  const vedeCompetenze = puoAccedere(utente, 'competenze', 'lettura');
  const [esitoTravaso, setEsitoTravaso] = useState(null); // { create, saltate }
  const [travasoInCorso, setTravasoInCorso] = useState(false);
  const travasoRef = useRef(false);
  const [mese, setMese] = useState(meseCorrente());
  const [tuttiMesi, setTuttiMesi] = useState(false);
  const [filtri, setFiltri] = useState({ ditta: null, tipo_id: '', categoria_id: '', operatore_id: '', origine: '', cerca: '' });
  const [ordine, setOrdine] = useState({ ordina: 'riferimento', verso: 'asc' });
  const [dati, setDati] = useState({ scadenze: [], totale: 0 });
  const [tipi, setTipi] = useState({ tipi: [], categorie: [] });
  const [operatori, setOperatori] = useState([]);
  const [selezionate, setSelezionate] = useState([]);
  const [inModifica, setInModifica] = useState(null); // null | 'nuova' | riga
  const [messaggio, setMessaggio] = useState('');
  const [errore, setErrore] = useState('');
  const [evidenziate, setEvidenziate] = useState([]);

  const parametri = useMemo(() => ({
    // Tutte le pratiche dell'anno: l'anno è l'unico parametro; altrimenti mese e anno scelti
    ...(tuttiMesi ? { anno: mese.slice(0, 4) } : { mese }),
    cliente_id: filtri.ditta?.id,
    tipo_id: filtri.tipo_id || undefined,
    categoria_id: filtri.categoria_id || undefined,
    operatore_id: filtri.operatore_id || undefined,
    origine: filtri.origine || undefined,
    cerca: filtri.cerca.trim() || undefined,
    ...ordine,
  }), [mese, tuttiMesi, filtri, ordine]);

  const ultimaRichiesta = useRef(0);
  const versioni = useVersioniViste();   // le eliminazioni mandano la versione che l'operatore ha visto davvero, non quella dell'ultimo aggiornamento automatico
  const carica = useCallback(async ({ silenzioso = false } = {}) => {
    const numero = ++ultimaRichiesta.current;
    try {
      const res = await api.get('/scadenze', { params: parametri });
      // Una risposta arrivata in ritardo (mese o filtri cambiati nel frattempo) non deve sostituire quella più recente
      if (numero !== ultimaRichiesta.current) return;
      versioni.aggiorna(res.data.scadenze, { silenzioso });
      setDati(res.data);
      setSelezionate(sel => sel.filter(id => res.data.scadenze.some(s => s.id === id)));
    } catch (err) {
      setErrore(err.response?.data?.errore || 'Errore nel caricamento');
    }
  }, [parametri, versioni]);

  const [ricarica, setRicarica] = useState(0);   // richiede un nuovo caricamento anche se mese e filtri non cambiano
  useEffect(() => { carica(); }, [carica, ricarica]);
  useEffect(() => {
    api.get('/scadenze/tipi').then(r => setTipi(r.data)).catch(() => {});
    api.get('/scadenze/operatori').then(r => setOperatori(r.data)).catch(() => {});
  }, []);
  useAggiornamentoPeriodico(() => carica({ silenzioso: true }), { attivo: !inModifica });

  const avvisa = t => { setMessaggio(t); setTimeout(() => setMessaggio(''), 8000); };
  const aggiornaFiltro = (campo, valore) => setFiltri(f => ({ ...f, [campo]: valore, ...(campo === 'tipo_id' ? { categoria_id: '' } : {}) }));
  const ordinaPer = chiave => setOrdine(o => ({ ordina: chiave, verso: o.ordina === chiave && o.verso === 'asc' ? 'desc' : 'asc' }));

  const portaInCompetenze = async (ids, giaInviate = 0) => {
    if (travasoInCorso || travasoRef.current) return; // doppio clic: una richiesta alla volta (il riferimento vale subito, lo stato solo al ridisegno)
    travasoRef.current = true;
    setErrore('');
    setTravasoInCorso(true);
    try {
      const res = await api.post('/competenze/da-scadenziario', { ids });
      // le righe saltate perché già in Competenze (es. inviate da un altro utente nel frattempo) si contano con le altre
      const giaOra = res.data.saltate.filter(x => /già in Competenze/.test(x.motivo));
      setEsitoTravaso({ create: res.data.create, saltate: res.data.saltate.filter(x => !giaOra.includes(x)), gia: giaInviate + giaOra.length });
      setSelezionate([]);
      carica();
    } catch (err) { setErrore(err.response?.data?.errore || 'Non è stato possibile portare le pratiche in Competenze'); } finally { travasoRef.current = false; setTravasoInCorso(false); }
  };

  // Pulsante sopra la tabella: le righe già inviate non si reinviano, si contano nel messaggio finale
  const inviaSelezionate = async () => {
    const scelte = dati.scadenze.filter(r => selezionate.includes(r.id) && r.categoria_collocamento);
    const gia = scelte.filter(r => r.competenza_id).length;
    const da = scelte.filter(r => !r.competenza_id).map(r => r.id);
    if (!da.length) { setEsitoTravaso({ create: [], saltate: [], gia }); setSelezionate([]); return; }
    await portaInCompetenze(da, gia);
  };

  const elimina = async (r) => {
    if (!window.confirm(`Spostare nel Cestino la voce di ${r.ditta}${[r.categoria, r.lavoratore].filter(Boolean).length ? ` (${[r.categoria, r.lavoratore].filter(Boolean).join(' · ')})` : ''}?`)) return;
    try { await api.delete(urlEliminazione(`/scadenze/${r.id}`, versioni.versioneVista(r))); carica(); } catch (err) { setErrore(avvisaConflitto(err, messaggioEliminazione(err))); if (eConflitto(err)) carica(); }
  };

  const esporta = async (tipo) => {
    setErrore('');
    try {
      await scarica(`/scadenze/export/${tipo}`, parametri, `scadenziario_${tuttiMesi ? mese.slice(0, 4) : mese}.${tipo === 'excel' ? 'xlsx' : 'pdf'}`);
    } catch { setErrore('Esportazione non riuscita'); }
  };

  const categorieFiltro = tipi.categorie.filter(c => !filtri.tipo_id || String(c.tipo_id) === String(filtri.tipo_id));
  // Cessazioni e trasformazioni: la data nella colonna "Inizio rapporto" è quella della cessazione / trasformazione
  const etichettaDal = r => tipi.campi?.categorie?.[r.categoria_chiave]?.etichette?.data_dal || '';
  const tutteSelezionate = dati.scadenze.length > 0 && selezionate.length === dati.scadenze.length;

  return (
    <Layout>
      <div style={stili.topBar} className="noStampa">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0 }}>Scadenziario</h2>
          <SelettorePeriodo mese={mese} tuttiMesi={tuttiMesi} onMese={setMese} onTuttiMesi={setTuttiMesi} descrizione="pratiche" />
        </div>
        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
          {puoScrivere && <button onClick={() => setInModifica('nuova')} className="btnTesta btnTestaPrimario">+ Nuova riga</button>}
          <button onClick={() => esporta('excel')} className="btnTesta">Esporta Excel</button>
          <button onClick={() => esporta('pdf')} className="btnTesta">Esporta PDF</button>
          <button onClick={() => window.print()} className="btnTesta">Stampa</button>
        </div>
      </div>

      {puoScrivere && <UnilavCarica tipi={tipi} onFinito={(salvate, meseScelto) => {
        // Dopo il salvataggio la vista va al mese della riga (o a quello scelto col pulsante) e la evidenzia
        if (salvate && salvate.length) {
          const mesi = [...new Set(salvate.map(x => x.mese).filter(Boolean))];
          setTuttiMesi(false);
          if (meseScelto) setMese(meseScelto); else if (mesi.length) setMese(mesi[0]);
          setFiltri(f => ({ ...f, ditta: null, tipo_id: '', categoria_id: '', operatore_id: '', origine: '', cerca: '' }));
          setEvidenziate(salvate.map(x => x.id));
          setTimeout(() => setEvidenziate([]), 15000);
          avvisa(`Salvata nel mese di ${mesi.map(nomeMese).join(', ')}`);
        }
        // Il caricamento parte con il mese e i filtri nuovi (prima partiva anche uno con quelli vecchi e poteva arrivare per ultimo)
        setRicarica(n => n + 1);
      }} />}

      {errore && <div style={stili.errore}>{errore}</div>}
      {messaggio && <div style={stiliPagina.ok}>{messaggio}</div>}

      <div style={stiliPagina.filtri} className="noStampa">
        <div style={{ flex: '2 1 200px' }}>
          {filtri.ditta ? (
            <div style={stiliPagina.chip}>
              {filtri.ditta.ragione_sociale}
              <button onClick={() => aggiornaFiltro('ditta', null)} style={stiliPagina.chipX} aria-label="Togli filtro azienda">×</button>
            </div>
          ) : (
            <ClienteRicerca url="/scadenze/ditte" placeholder="Filtra per azienda…" onSeleziona={d => aggiornaFiltro('ditta', d)} />
          )}
        </div>
        <select value={filtri.tipo_id} onChange={e => aggiornaFiltro('tipo_id', e.target.value)} style={stiliPagina.select} aria-label="Tipo">
          <option value="">Tutti i tipi</option>
          {tipi.tipi.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
        </select>
        <select value={filtri.categoria_id} onChange={e => aggiornaFiltro('categoria_id', e.target.value)} style={stiliPagina.select} aria-label="Categoria">
          <option value="">Tutte le categorie</option>
          {categorieFiltro.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
        </select>
        <select value={filtri.operatore_id} onChange={e => aggiornaFiltro('operatore_id', e.target.value)} style={stiliPagina.select} aria-label="Operatore">
          <option value="">Tutti gli operatori</option>
          {operatori.map(o => <option key={o.id} value={o.id}>{o.nome} {o.cognome}</option>)}
        </select>
        <select value={filtri.origine} onChange={e => aggiornaFiltro('origine', e.target.value)} style={stiliPagina.select} aria-label="Manuale o UNILAV">
          <option value="">Manuale e UNILAV</option>
          {Object.entries(FILTRO_ORIGINI).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <input type="search" placeholder="Cerca nel testo…" value={filtri.cerca} onChange={e => aggiornaFiltro('cerca', e.target.value)} style={{ ...stiliPagina.select, flex: '1 1 160px' }} />
      </div>

      {puoPortare && selezionate.length > 0 && (
        <div style={stiliPagina.blocco} className="noStampa">
          <strong>{selezionate.length} selezionate</strong>
          {puoPortare && dati.scadenze.some(r => selezionate.includes(r.id) && r.categoria_collocamento) && (
            <button onClick={inviaSelezionate} disabled={travasoInCorso} style={{ ...stili.btn, background: '#D3D678', color: '#1a1a1a', fontWeight: 600 }}>{travasoInCorso ? 'Invio in corso…' : 'Invia a competenze'}</button>
          )}
          <button onClick={() => setSelezionate([])} style={stili.btnSmall}>Annulla selezione</button>
        </div>
      )}

      {esitoTravaso && (
        <div style={{ border: '1px solid #D3D678', background: '#F7F7EA', borderRadius: '8px', padding: '0.7rem 0.9rem', marginBottom: '0.8rem', fontSize: '0.88rem' }} className="noStampa" role="status">
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
            <strong>{esitoTravaso.create.length} inviate, {esitoTravaso.gia || 0} già in Competenze{esitoTravaso.saltate.length ? `, ${esitoTravaso.saltate.length} non inviate` : ''}</strong>
            <button onClick={() => setEsitoTravaso(null)} style={stili.btnSmall}>Chiudi</button>
          </div>
          {esitoTravaso.create.length > 0 && (
            <ul style={{ margin: '0.4rem 0 0', paddingLeft: '1.2rem', color: '#166534' }}>
              {esitoTravaso.create.map(c => <li key={c.scadenza_id}>{c.descrizione}{vedeCompetenze && <> · <Link to={`/competenze?mese=${c.mese}&evidenzia=${c.competenza_id}`}>apri in Competenze</Link></>}</li>)}
            </ul>
          )}
          {esitoTravaso.saltate.length > 0 && (
            <ul style={{ margin: '0.4rem 0 0', paddingLeft: '1.2rem', color: '#9a3412' }}>
              {esitoTravaso.saltate.map(x => <li key={x.scadenza_id}><strong>{x.riferimento}</strong>: {x.motivo}</li>)}
            </ul>
          )}
        </div>
      )}

      <h3 className="soloStampa">Scadenziario · {tuttiMesi ? `anno ${mese.slice(0, 4)}` : nomeMese(mese)}</h3>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ ...stili.table, tableLayout: 'fixed' }} className="tabellaCompetenze tabellaScadenziario">
          <thead>
            <tr>
              {puoPortare && (
                <th style={{ ...stili.th, width: '3%' }} className="noStampa">
                  <input type="checkbox" aria-label="Seleziona tutte" checked={tutteSelezionate}
                    onChange={e => setSelezionate(e.target.checked ? dati.scadenze.map(s => s.id) : [])} />
                </th>
              )}
              {COLONNE.map(c => (
                <th key={c.label} className={c.chiave === 'codice_azienda' ? 'intCodiceAz' : undefined} style={{ ...stili.th, cursor: c.chiave ? 'pointer' : 'default', ...(c.larghezza ? { width: c.larghezza } : {}) }}
                  onClick={c.chiave ? () => ordinaPer(c.chiave) : undefined}
                  aria-sort={ordine.ordina === c.chiave ? (ordine.verso === 'asc' ? 'ascending' : 'descending') : undefined}>
                  {c.label}{ordine.ordina === c.chiave ? (ordine.verso === 'asc' ? ' ▲' : ' ▼') : ''}
                </th>
              ))}
              <th style={stili.th} className="soloStampaCella">Fatto</th>
              {(puoScrivere || puoPortare) && <th style={{ ...stili.th, width: '10%', textAlign: 'center' }} className="noStampa">Azioni</th>}
            </tr>
          </thead>
          <tbody>
            {dati.scadenze.map(r => (
              <tr key={r.id} style={evidenziate.includes(r.id) ? { ...stili.tr, background: '#F7F7EA', outline: '2px solid #D3D678' } : (selezionate.includes(r.id) ? { ...stili.tr, background: '#F7F7EA' } : stili.tr)} ref={el => { if (el && evidenziate[0] === r.id) el.scrollIntoView({ block: 'center' }); }}>
                {puoPortare && (
                  <td style={stili.td} className="noStampa">
                    <input type="checkbox" aria-label={`Seleziona voce ${r.id}`} checked={selezionate.includes(r.id)}
                      onChange={e => setSelezionate(s => (e.target.checked ? [...s, r.id] : s.filter(x => x !== r.id)))} />
                  </td>
                )}
                <td style={stili.td} className="cellaCodiceAz">{r.codice_azienda ?? r.codice_ditta_paghe ?? ''}</td>
                <td style={stili.td}>{r.ditta}{versioni.cambiata(r) && <ModificataDaAltri onRiconosci={() => versioni.riconosci(r)} />}</td>
                <td style={stili.td}>{r.lavoratore}</td>
                <td style={stili.td}>{matricola(r)}</td>
                <td style={stili.td}>
                  {r.categoria || <span style={{ color: '#888' }}>{r.tipo}</span>}
                  {r.categoria_chiave === 'trasformazione' && r.tipo_trasformazione && <div className="dataNota">{etichettaTipoTrasformazione(r.tipo_trasformazione)}</div>}
                </td>
                <td style={stili.td}>{TIPI_CONTRATTO[tipologiaDaRiga(r)] || ''}</td>
                <td style={stili.td} className="dataCella" title={formattaData(r.data_dal)}>
                  {dataBreve(r.data_dal)}
                  {r.data_dal && etichettaDal(r) && <div className="dataNota">{etichettaDal(r)}</div>}
                </td>
                {/* La scadenza si mostra solo per i contratti a termine */}
                <td style={stili.td} className="dataCella" title={formattaData(r.data_scadenza)}>{r.tipo_contratto === 'tempo_indeterminato' ? '' : dataBreve(r.data_scadenza)}</td>
                <td style={stili.td}>{r.orario ? ORARI[r.orario] : (r.tipo_contratto === 'intermittente' ? NON_DEFINITO : '')}</td>
                <td style={stili.td}>{r.ore_settimanali ?? (r.tipo_contratto === 'intermittente' ? NON_DEFINITO : '')}</td>
                <td style={stili.td}>{r.stagionale === true ? 'Sì' : r.stagionale === false ? 'No' : ''}</td>
                <td style={{ ...stili.td, whiteSpace: 'pre-line' }}>
                  {r.note}
                  {r.competenza_id && (
                    <div style={{ fontSize: '0.74rem', color: '#555' }}>
                      <span style={stiliPagina.origine}>già in Competenze</span>
                      {vedeCompetenze && <> <Link to={`/competenze?mese=${r.competenza_mese}&evidenzia=${r.competenza_id}`}>apri</Link></>}
                      {r.modificata_dopo_travaso && <span style={{ color: '#9a3412', marginLeft: '0.4rem' }} title="La pratica è cambiata dopo il travaso (azienda, categoria, lavoratore o data pratica): la competenza non si aggiorna da sola">modificata dopo il travaso</span>}
                    </div>
                  )}
                </td>
                <td style={stili.td}>
                  <span style={stiliPagina.origine}>{ORIGINI[r.origine]}</span>
                  <div style={{ color: '#555', fontSize: '0.78rem', marginTop: '0.2rem' }}>{r.inserita_da}</div>
                </td>
                <td style={stili.td} className="soloStampaCella"><span className="casellaFatto" /></td>
                {(puoScrivere || puoPortare) && (
                  <td style={stili.td} className="noStampa">
                    <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', justifyContent: 'center' }}>
                      {puoScrivere && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', alignItems: 'flex-start' }}>
                          <button onClick={() => setInModifica(r)} style={{ ...stili.btnSmall, marginRight: 0, whiteSpace: 'nowrap' }}>Modifica</button>
                          <button onClick={() => elimina(r)} style={{ ...stili.btnSmallDanger, whiteSpace: 'nowrap' }}>Elimina</button>
                        </div>
                      )}
                      {puoPortare && (r.categoria_collocamento
                        ? <BottoneInvio inviata={Boolean(r.competenza_id)} occupato={travasoInCorso} onInvia={() => portaInCompetenze([r.id])} />
                        : <span className="bottoneInvioVuoto" aria-hidden="true" />)}
                    </div>
                  </td>
                )}
              </tr>
            ))}
            {dati.scadenze.length === 0 && (
              <tr><td colSpan={17} style={{ ...stili.td, textAlign: 'center', color: '#999', padding: '1.5rem' }}>Nessuna voce per questi filtri</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p style={stiliPagina.totali}>{dati.totale} voci</p>

      {inModifica && (
        <FormScadenza
          scadenza={inModifica === 'nuova' ? null : inModifica}
          tipi={tipi}
          meseIniziale={tuttiMesi ? meseCorrente() : mese}
          onChiudi={() => setInModifica(null)}
          onSalvato={(testo) => { setInModifica(null); avvisa(testo); carica(); }}
        />
      )}
    </Layout>
  );
}

// Nuova riga o modifica: gli stessi campi per tutte le origini
function FormScadenza({ scadenza, tipi, meseIniziale, onChiudi, onSalvato }) {
  const daRiga = r => ({
    ditta: r ? { id: r.cliente_id, ragione_sociale: r.ditta } : null,
    tipo_id: r?.tipo_id || tipi.tipi[0]?.id || '',
    categoria_id: r?.categoria_id || '',
    lavoratore: r?.lavoratore || '', // testo di una riga salvata prima dei campi separati
    lavoratore_cognome: r?.lavoratore_cognome || '',
    lavoratore_nome: r?.lavoratore_nome || '',
    matricola: r?.matricola || '',
    tipo_contratto: tipologiaDaRiga(r),
    tipo_trasformazione: r ? scriviTipoTrasformazione(leggiTipoTrasformazione(r.tipo_trasformazione).scelta, leggiTipoTrasformazione(r.tipo_trasformazione).testo) : '',
    orario: r?.orario || '',
    ccnl_codice: r?.ccnl_codice || '',
    livello: r?.livello || '',
    mansione: r?.mansione || '',
    stagionale: r && (r.stagionale === true || r.stagionale === false) ? r.stagionale : null,
    ore_settimanali: r?.ore_settimanali ?? '',
    data_dal: r?.data_dal || '',
    data_evento: r ? (r.data_evento || '') : (meseIniziale === meseCorrente() ? oggi() : `${meseIniziale}-01`),
    mese_elaborazione: r?.mese_riferimento || meseIniziale,
    data_scadenza: r?.data_scadenza || '',
    stato: r?.stato || 'da_fare',
    motivo_verifica: r?.motivo_verifica || '',
    note: r?.note || '',
    version: r?.version,
  });
  const [form, setForm] = useState(daRiga(scadenza));
  // Il mese di elaborazione segue la data evento finché non lo si cambia a mano
  const [meseManuale, setMeseManuale] = useState(Boolean(scadenza));
  const puoScrivere = usePuoScrivere('scadenziario');
  const [ccnlElenco, setCcnlElenco] = useState([]);
  const ricaricaCcnl = () => api.get('/scadenze/unilav/ccnl').then(r => setCcnlElenco(r.data.ccnl)).catch(() => {});
  useEffect(() => { ricaricaCcnl(); }, []);
  const [errore, setErrore] = useState('');
  const [salvataggio, setSalvataggio] = useState(false);
  const occupato = useRef(false);   // doppio clic: lo stato React si aggiorna solo al ridisegno
  const [conflitto, setConflitto] = useState(null);
  const [nuovaDitta, setNuovaDitta] = useState(null);
  const tipoScelto = tipi.tipi.find(t => String(t.id) === String(form.tipo_id));
  const senzaDittaAmmessa = tipoScelto?.chiave === 'promemoria';
  const aggiorna = (campo, valore) => setForm(f => ({
    ...f,
    [campo]: valore,
    ...(campo === 'tipo_id' ? { categoria_id: '' } : {}),
  }));
  // Il mese segue Dal (se compilato) o la Data pratica finché non lo si cambia a mano
  const impostaData = (campo, valore) => setForm(f => {
    const nuovo = { ...f, [campo]: valore };
    const da = nuovo.data_dal || nuovo.data_evento;
    return { ...nuovo, ...(!meseManuale && da ? { mese_elaborazione: da.slice(0, 7) } : {}) };
  });

  const regola = regolaDi(tipi, form.tipo_id, form.categoria_id);
  const mostra = regola.mostra;
  const richiesti = richiestiConCondizioni(regola, form);
  const etichetta = (testo, nome) => `${testo}${richiesti.has(nome) ? ' *' : ''}`;
  const rigaOre = mostra.has('tipo_trasformazione');   // trasformazione: Tipo contratto, Ore settimanali e Tipo trasformazione nella stessa riga
  const intermittente = eIntermittente(form.tipo_contratto);
  // Intermittente: orario e ore "Non definito"; indeterminato: nessuna scadenza
  const cambiaContratto = v => setForm(f => ({ ...f, tipo_contratto: v, ...(eIntermittente(v) ? { ore_settimanali: '', orario: '' } : {}), ...(v === 'intermittente_indet' ? { data_scadenza: '' } : {}) }));
  const cambiaOrario = v => setForm(f => ({ ...f, orario: v, ...(v === 'full_time' && !(Number(f.ore_settimanali) > 0) ? { ore_settimanali: 40 } : {}) }));
  const selectOrario = mostra.has('orario') && (
    <div style={stili.campo}>
      <label style={stili.label}>{etichetta('Orario', 'orario')}</label>
      <select value={intermittente ? '' : form.orario} disabled={intermittente} onChange={e => cambiaOrario(e.target.value)} style={stili.input}>
        {intermittente ? <option value="">{NON_DEFINITO}</option> : <><option value="">—</option><option value="full_time">{ORARI.full_time}</option><option value="part_time">{ORARI.part_time}</option></>}
      </select>
    </div>
  );
  const selectContratto = (
    <div style={stili.campo}>
      <label style={stili.label}>{etichetta('Tipologia', 'tipo_contratto')}</label>
      <select value={form.tipo_contratto} onChange={e => cambiaContratto(e.target.value)} style={stili.input}>
        <option value="">—</option>
        {Object.entries(TIPI_CONTRATTO).map(([k, n]) => <option key={k} value={k}>{n}</option>)}
      </select>
    </div>
  );

  const salva = async (version) => {
    setErrore('');
    const m = mancanti(tipi, regola, form);
    if (m.length) return setErrore(`Mancano: ${m.join(', ')}`);
    if (!form.ditta && !senzaDittaAmmessa) return setErrore("Scegli l'azienda dall'anagrafica (solo i promemoria generali non ne hanno bisogno)");
    if (!form.data_evento && !form.mese_elaborazione) return setErrore("Indica la data pratica, il campo Dal o il mese di elaborazione");
    if (occupato.current) return;
    occupato.current = true;
    setSalvataggio(true);
    const dati = {
      ...form, cliente_id: form.ditta?.id || null, categoria_id: form.categoria_id || null,
      data_evento: form.data_evento || null, data_dal: form.data_dal || null, data_scadenza: form.data_scadenza || null, version,
      ore_settimanali: form.ore_settimanali === '' ? null : form.ore_settimanali, stagionale: form.stagionale,
    };
    delete dati.ditta;
    try {
      if (scadenza) await api.put(`/scadenze/${scadenza.id}`, dati);
      else await api.post('/scadenze', dati);
      setConflitto(null);
      onSalvato(scadenza ? 'Voce aggiornata' : 'Voce inserita');
    } catch (err) {
      if (eConflitto(err)) setConflitto(err.response.data);
      else setErrore(err.response?.data?.errore || 'Errore nel salvataggio');
    } finally {
      occupato.current = false; setSalvataggio(false);
    }
  };

  const categorie = tipi.categorie.filter(c => String(c.tipo_id) === String(form.tipo_id) && c.attiva);
  // Per le variazioni paghe con una categoria scelta valgono le regole della tabella; per gli altri tipi nessun campo nuovo
  function regolaDelTipo(t, f) { return regolaDi(t, f.tipo_id, f.categoria_id).mostra.size > 0; }

  return (
    <div style={stili.overlay}>
      <div style={{ ...stili.modal, maxWidth: '700px' }}>
        <h3 style={{ marginTop: 0 }}>{scadenza ? 'Modifica voce' : 'Nuova riga'}</h3>
        {scadenza && <p style={{ marginTop: '-0.6rem', color: '#777', fontSize: '0.8rem' }}>Origine: {ORIGINI[scadenza.origine]} · inserita da {scadenza.inserita_da || '—'}</p>}
        <form onSubmit={e => { e.preventDefault(); salva(form.version); }}>
          {errore && <div style={stili.errore}>{errore}</div>}
          <div style={stili.campo}>
            <label style={stili.label}>Azienda{senzaDittaAmmessa ? ' (facoltativa per i promemoria generali dello studio)' : ''}</label>
            <ClienteRicerca url="/scadenze/ditte" valore={form.ditta} placeholder="Cerca azienda…"
              onSeleziona={d => aggiorna('ditta', d)} onNuovo={testo => setNuovaDitta(testo)} />
            {senzaDittaAmmessa && form.ditta && (
              <button type="button" onClick={() => aggiorna('ditta', null)} style={{ ...stili.btnSmall, marginTop: '0.3rem' }}>Togli l'azienda (promemoria generale)</button>
            )}
          </div>
          <div style={stili.row}>
            <div style={stili.campo}>
              <label style={stili.label}>Tipo</label>
              <select value={form.tipo_id} onChange={e => aggiorna('tipo_id', e.target.value)} style={stili.input} required>
                {tipi.tipi.filter(t => t.attivo).map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
              </select>
            </div>
            <div style={stili.campo}>
              <label style={stili.label}>Categoria</label>
              <select value={form.categoria_id} onChange={e => aggiorna('categoria_id', e.target.value)} style={stili.input}>
                <option value="">—</option>
                {categorie.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
            </div>
          </div>
          <div style={stili.row}>
            <div style={stili.campo}>
              <label style={stili.label}>{regola.richiesti.has('lavoratore') ? 'Cognome *' : 'Cognome'}</label>
              <input value={form.lavoratore_cognome} onChange={e => aggiorna('lavoratore_cognome', e.target.value)} style={stili.input} />
            </div>
            <div style={stili.campo}>
              <label style={stili.label}>{regola.richiesti.has('lavoratore') ? 'Nome *' : 'Nome'}</label>
              <input value={form.lavoratore_nome} onChange={e => aggiorna('lavoratore_nome', e.target.value)} style={stili.input} />
            </div>
            {(mostra.has('matricola') || !regola.mostra.size) && (
              <div style={stili.campo}>
                <label style={stili.label}>{etichetta('Matricola', 'matricola')}</label>
                <input value={form.matricola} onChange={e => aggiorna('matricola', e.target.value)} style={stili.input} placeholder="es. 226" />
              </div>
            )}
          </div>
          {!form.lavoratore_cognome && !form.lavoratore_nome && form.lavoratore && (
            <p style={{ margin: '-0.4rem 0 0.8rem', fontSize: '0.8rem', color: '#777' }}>
              Lavoratore salvato prima dei campi separati: «{form.lavoratore}». Resta così finché non compili Cognome e Nome.
            </p>
          )}
          {(mostra.has('tipo_contratto') && !rigaOre) || mostra.has('stagionale') ? (
            <div style={stili.row}>
              {mostra.has('tipo_contratto') && !rigaOre && selectContratto}
              {mostra.has('stagionale') && (
                <div style={stili.campo}>
                  <label style={stili.label}>{etichetta('Stagionale', 'stagionale')}</label>
                  <select value={form.stagionale === true ? 'si' : form.stagionale === false ? 'no' : ''} onChange={e => aggiorna('stagionale', e.target.value === 'si' ? true : e.target.value === 'no' ? false : null)} style={stili.input}>
                    <option value="">—</option>
                    <option value="si">Sì</option>
                    <option value="no">No</option>
                  </select>
                </div>
              )}
            </div>
          ) : null}
          {(mostra.has('ccnl_codice') || mostra.has('livello') || mostra.has('mansione')) && (
            <div style={stili.row}>
              {mostra.has('ccnl_codice') && <SceltaCcnl id="form-ccnl" valore={form.ccnl_codice} onCambia={c => aggiorna('ccnl_codice', c)} elenco={ccnlElenco} ricarica={ricaricaCcnl} puoScrivere={puoScrivere} obbligatorio={richiesti.has('ccnl_codice')} />}
              {mostra.has('livello') && (
                <div style={stili.campo}>
                  <label style={stili.label}>{etichetta('Livello', 'livello')}</label>
                  <input value={form.livello} maxLength={60} onChange={e => aggiorna('livello', e.target.value)} style={stili.input} placeholder="es. 6, IV, Quadro" />
                </div>
              )}
              {mostra.has('mansione') && (
                <div style={stili.campo}>
                  <label style={stili.label}>{etichetta('Mansione', 'mansione')}</label>
                  <input value={form.mansione} maxLength={200} onChange={e => aggiorna('mansione', e.target.value)} style={stili.input} placeholder="es. cuoco" />
                </div>
              )}
            </div>
          )}
          {mostra.has('ore_settimanali') && (
            <div style={stili.row}>
              {rigaOre && selectContratto}
              {selectOrario}
              <div style={stili.campo}>
                <label style={stili.label}>{etichetta('Ore settimanali', 'ore_settimanali')}</label>
                <input type={intermittente ? 'text' : 'number'} min="0" step="0.5" disabled={intermittente} value={intermittente ? NON_DEFINITO : form.ore_settimanali} onChange={e => aggiorna('ore_settimanali', e.target.value === '' ? '' : Number(e.target.value))} style={stili.input} />
              </div>
              {rigaOre ? (
                <CampoTipoTrasformazione id="form-trasf" valore={form.tipo_trasformazione} onCambia={v => aggiorna('tipo_trasformazione', v)}
                  etichetta={etichetta('Tipo di trasformazione', 'tipo_trasformazione')} daControllare={richiesti.has('tipo_trasformazione') && !String(form.tipo_trasformazione || '').trim()} />
              ) : <div style={{ flex: 2 }} />}
            </div>
          )}
          <div style={stili.row}>
            <div style={stili.campo}>
              <label style={stili.label}>{regolaDelTipo(tipi, form) ? etichetta('Data pratica', 'data_evento') : 'Data pratica (facoltativa)'}</label>
              <input type="date" value={form.data_evento} onChange={e => impostaData('data_evento', e.target.value)} style={stili.input} />
            </div>
            {(mostra.has('data_dal') || !regola.mostra.size) && (
              <div style={{ ...stili.campo, ...(regola.etichette.data_dal ? { order: -1 } : {}) }}>
                <label style={stili.label}>{etichetta(regola.etichette.data_dal || 'Dal', 'data_dal')}</label>
                <input type="date" value={form.data_dal} onChange={e => impostaData('data_dal', e.target.value)} style={stili.input} />
              </div>
            )}
            <div style={stili.campo}>
              <label style={stili.label}>Mese di elaborazione</label>
              <input type="month" value={form.mese_elaborazione} onChange={e => { setMeseManuale(true); setForm(f => ({ ...f, mese_elaborazione: e.target.value })); }} style={stili.input} />
            </div>
            {(mostra.has('data_scadenza') || !regola.mostra.size) && (
              <div style={stili.campo}>
                <label style={stili.label}>{etichetta('Data scadenza', 'data_scadenza')}</label>
                <input type="date" value={form.data_scadenza} onChange={e => aggiorna('data_scadenza', e.target.value)} style={stili.input} />
              </div>
            )}
          </div>
          <div style={stili.campo}>
            <label style={stili.label}>Note</label>
            <input value={form.note} onChange={e => aggiorna('note', e.target.value)} style={stili.input} />
          </div>
          <div style={stili.azioni}>
            <button type="button" onClick={onChiudi} style={stili.btnSecondary}>Annulla</button>
            <button type="submit" disabled={salvataggio} style={stili.btn}>{salvataggio ? 'Salvataggio…' : 'Salva'}</button>
          </div>
        </form>
      </div>
      {conflitto && (
        <ConflittoVersione
          conflitto={conflitto}
          inCorso={salvataggio}
          onRicarica={() => { setForm(daRiga(conflitto.attuale)); setConflitto(null); }}
          onSovrascrivi={() => salva(conflitto.attuale.version)}
        />
      )}
      {nuovaDitta !== null && (
        <FormDitta
          ragioneIniziale={nuovaDitta}
          onChiudi={() => setNuovaDitta(null)}
          onSalvato={d => { aggiorna('ditta', d); setNuovaDitta(null); }}
        />
      )}
    </div>
  );
}

const stiliPagina = {
  freccia: { background: 'white', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', fontSize: '1.1rem', padding: '0.1rem 0.6rem' },
  ok: { background: '#ecfdf5', color: '#166534', padding: '0.6rem', borderRadius: '6px', marginBottom: '0.8rem', fontSize: '0.88rem' },
  filtri: { display: 'flex', gap: '0.5rem', marginBottom: '0.8rem', flexWrap: 'wrap', alignItems: 'center' },
  select: { padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.88rem', background: 'white' },
  check: { display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.88rem', whiteSpace: 'nowrap' },
  chip: { display: 'inline-flex', alignItems: 'center', gap: '0.3rem', background: '#F7F7EA', border: '1px solid #D3D678', borderRadius: '999px', padding: '0.35rem 0.4rem 0.35rem 0.8rem', fontSize: '0.88rem' },
  chipX: { border: 'none', background: 'none', cursor: 'pointer', fontSize: '1rem', color: '#555' },
  blocco: { display: 'flex', alignItems: 'center', gap: '0.6rem', background: '#232323', color: 'white', padding: '0.6rem 0.9rem', borderRadius: '8px', marginBottom: '0.8rem', flexWrap: 'wrap' },
  origine: { fontSize: '0.72rem', color: '#555', background: '#f3f3f1', padding: '0.1rem 0.4rem', borderRadius: '4px' },
  totali: { color: '#555', fontSize: '0.88rem', marginTop: '0.6rem' },
};
